import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Eye, FileText, Calendar, User, LogOut, Download, Coins, Shield, Lock, Users } from 'lucide-react';
import { cookieManager } from '../../utils/cookieManager.js';
import { getApiBaseUrl } from '../../utils/apiConfig.js';
import {
  getUserAllForms,
  deleteForm,
  getStatusText,
  getStatusStyles,
  createForm,
  getSpecificForm
} from '../../services/formManagementService.js';
import formDataStorage from '../../utils/formDataStorage.js';
import CreateApplicationModal from './CreateApplicationModal';
import ChangePasswordForm from '../auth/ChangePasswordForm';
import Footer from './Footer';
import UserOverview from './UserOverview';

// 申请管理组件
const ApplicationManager = () => {
  const navigate = useNavigate();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // 获取用户邮箱
  const getUserEmail = () => {
    try {
      return cookieManager.getUserEmail() || '用户';
    } catch (error) {
      return '用户';
    }
  };

  // 检查用户是否是管理员
  const checkIsAdmin = async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/auth/me`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });

      if (response.ok) {
        const userData = await response.json();
        setIsAdmin(userData.role === 'admin');
      }
    } catch (error) {
      console.error('获取用户信息失败:', error);
      setIsAdmin(false);
    }
  };

  // 加载用户的所有申请
  const loadApplications = async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await getUserAllForms(true);

      if (result.success) {
        const formattedform = result.data.map(app => ({
          ...app,
        }));
        setForms(formattedform);
      } else {
        setError(result.error || '加载申请失败');
      }
    } catch (error) {
      setError('加载申请时发生错误');
      console.error('加载申请失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadConfirmation = async (form) => {
    let snapshot = form.confirmation_snapshot;

    if (!snapshot || !snapshot.data_url) {
      const detail = await getSpecificForm(form._id);
      if (detail.success && detail.data?.confirmation_snapshot) {
        snapshot = detail.data.confirmation_snapshot;
        setForms(prev => prev.map(item => (
          item._id === form._id
            ? { ...item, confirmation_snapshot: snapshot }
            : item
        )));
      }
    }

    if (!snapshot || !snapshot.data_url) {
      alert('确认页截图暂不可用，请稍后重试。');
      return;
    }

    try {
      const link = document.createElement('a');
      link.href = snapshot.data_url;
      const defaultName = `DS160_Confirmation_${snapshot.application_id || form.application_id || form.title || form._id}_${new Date().toISOString().slice(0, 10)}.png`;
      link.download = snapshot.filename || defaultName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('确认页下载失败:', error);
      alert('确认页截图下载失败，请稍后重试。');
    }
  };

  // 显示创建申请模态框
  const handleShowCreateModal = () => {
    setShowCreateModal(true);
  };

  // TODO: 处理创建申请
  const handleCreateApplication = async (form_data) => {
    try {
      console.log('🎯 [ApplicationManager] 开始处理创建申请');
      console.log('📝 [ApplicationManager] 接收到的表单数据:', form_data);

      // 导入表单配置
      const { baseFormPages } = await import('../../utils/formConfig.js');
      console.log('📚 [ApplicationManager] 基础表单页面配置:', baseFormPages);

      // 构建请求数据
      const requestData = {
        created_at: new Date().toISOString(),
        status: 'draft',
        ...form_data,
        completion_stats: {
          total_forms: baseFormPages.length,
          completed_forms: 0,
          completion_rate: '0.0',
          completed_form_ids: [],
          completed_form_titles: []
        }
      };

      // 使用新的方法创建表单并获取ObjectId
      const result = await createForm(requestData);

      console.log('📋 [ApplicationManager] 创建表单API返回结果:', result);

      // 修复：检查是否有有效的表单ID，而不是检查null
      if (result && result._id && result.success !== false) {
        console.log('✅ [ApplicationManager] 表单创建成功，准备跳转到填写页面');

        // 设置当前编辑的表单ID和标题到sessionStorage
        formDataStorage.saveFormData('form_id', result._id);
        formDataStorage.saveFormData('form_title', form_data.title);

        // 保存安全问题和答案到 formDataStorage，确保验证码提交时能够获取到
        if (form_data.security_question) {
          formDataStorage.saveFormData('security_question', form_data.security_question);
        }
        if (form_data.security_answer) {
          formDataStorage.saveFormData('security_answer', form_data.security_answer);
        }

        // 创建成功后跳转到表单填写页面
        console.log('🚀 [ApplicationManager] 跳转到表单填写页面:', `/application/${result._id}`);
        navigate(`/application/${result._id}`);
      } else {
        // 处理创建失败的情况
        console.error('❌ [ApplicationManager] 表单创建失败:', result);
        setError(result?.error || '创建申请失败');
      }
    } catch (error) {
      setError('创建申请时发生错误');
      console.error('创建申请失败:', error);
    }
  };

  // 编辑申请
  const handleEditApplication = async (form_id) => {
    try {
      navigate(`/form/${form_id}`);
    }
    catch (error) {
      setError('跳转失败');
      console.error('跳转失败:', error);
    }
  };

  // 查看申请详情
  const handleViewApplication = (form_id) => {
    navigate(`/application/${form_id}`);
  };

  // 删除申请
  const handleDeleteApplication = async (form_id) => {
    try {
      const result = await deleteForm(form_id);

      if (result.success) {
        // 删除成功后重新加载列表
        await loadApplications();
        setShowDeleteConfirm(null);
      } else {
        setError(result.error || '删除申请失败');
      }
    } catch (error) {
      setError('删除申请时发生错误');
      console.error('删除申请失败:', error);
    }
  };

  // 退出登录
  const handleLogout = async () => {
    try {
      // 调用后端登出 API，清除服务器端 session
      await fetch(`${getApiBaseUrl()}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });
    } catch (error) {
      console.error('后端登出失败:', error);
    } finally {
      // 清除前端数据
      cookieManager.clearAuthCookies();
      formDataStorage.clearAllFormData();
      navigate('/');
      // 强制刷新页面，确保退出登录后UI同步更新
      window.location.reload();
    }
  };

  // 格式化日期
  const formatDate = (dateString) => {
    if (!dateString) return '未知';
    try {
      return new Date(dateString).toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return '未知';
    }
  };

  // 组件加载时获取申请列表
  useEffect(() => {
    loadApplications();
    checkIsAdmin();
  }, []);

  return (
    <>
      <div className="min-h-screen bg-gray-50">
      {/* 头部 */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center space-x-4">
              <h1 className="text-2xl font-bold text-gray-900">用户中心</h1>
              <div className="flex items-center space-x-2 text-gray-600">
                <User className="w-4 h-4" />
                <span>{getUserEmail()}</span>
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <a
                href="https://lvbaobao.space"
                className="text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium"
              >
                返回首页
              </a>
              {/* 新增：入口快捷按钮（仅展示，不做权限校验，由后端接口控制权限） */}
              <button
                onClick={() => navigate('/points')}
                className="flex items-center space-x-1 text-emerald-700 hover:text-emerald-800 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 px-3 py-2 rounded-md text-sm font-medium"
                title="积分中心"
              >
                <Coins className="w-4 h-4" />
                <span>积分中心</span>
              </button>
              <button
                onClick={() => navigate('/commission/info')}
                className="flex items-center space-x-1 text-purple-700 hover:text-purple-800 bg-purple-50 hover:bg-purple-100 border border-purple-200 px-3 py-2 rounded-md text-sm font-medium"
                title="分销商中心"
              >
                <Users className="w-4 h-4" />
                <span>分销商中心</span>
              </button>
              <button
                onClick={() => setIsChangePasswordOpen(true)}
                className="flex items-center space-x-1 text-amber-700 hover:text-amber-800 bg-amber-50 hover:bg-amber-100 border border-amber-200 px-3 py-2 rounded-md text-sm font-medium"
                title="修改密码"
              >
                <Lock className="w-4 h-4" />
                <span>修改密码</span>
              </button>
              {isAdmin && (
                <>
                  <button
                    onClick={() => navigate('/admin/distributors')}
                    className="flex items-center space-x-1 text-orange-700 hover:text-orange-800 bg-orange-50 hover:bg-orange-100 border border-orange-200 px-3 py-2 rounded-md text-sm font-medium"
                    title="分销商审核"
                  >
                    <Users className="w-4 h-4" />
                    <span>分销商审核</span>
                  </button>
                  <button
                    onClick={() => navigate('/admin')}
                    className="flex items-center space-x-1 text-indigo-700 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 px-3 py-2 rounded-md text-sm font-medium"
                    title="管理后台"
                  >
                    <Shield className="w-4 h-4" />
                    <span>管理后台</span>
                  </button>
                </>
              )}
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 text-red-600 hover:text-red-700 px-3 py-2 rounded-md text-sm font-medium"
              >
                <LogOut className="w-4 h-4" />
                <span>退出登录</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <UserOverview />

      {/* 全局填写提示横幅 */}
	      <div className="bg-blue-50 border-b-2 border-blue-200 py-4">
	        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
	          <div className="flex items-start gap-3">
	            <div className="text-blue-600 mt-0.5">
	              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
	                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
	              </svg>
	            </div>
	            <div className="text-sm text-blue-800">
	              <p className="font-semibold mb-1">📝 填写提示</p>
	              <p>所有字段请使用<strong>英文字母</strong>填写。中文地址、姓名等中文内容请转换为<strong>拼音</strong>或<strong>英文</strong>。</p>
	            </div>
	          </div>
	        </div>
	      </div>

	      {/* 隐私提示 */}
	      <div className="bg-amber-50 border-b border-amber-200 py-3">
	        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
	          <p className="text-sm text-amber-900">
	            <strong>隐私提示：</strong>本页面仅展示DS-160申请记录；美国EVUS登记、越南E-Visa申请、泰国入境卡在线填写后台均不保存任何记录，请务必自行保存回执/确认页截图。
	          </p>
	        </div>
	      </div>

		      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
		        {/* 统计信息 */}
		        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
		          <div className="bg-white rounded-lg shadow p-6">
		            <div className="flex items-center">
	              <div className="p-2 bg-blue-100 rounded-lg">
	                <FileText className="w-6 h-6 text-blue-600" />
	              </div>
	              <div className="ml-4">
	                <p className="text-sm font-medium text-gray-600">总DS-160申请数</p>
	                <p className="text-2xl font-bold text-gray-900">{forms.length}</p>
	              </div>
	            </div>
	          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <FileText className="w-6 h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">已完成</p>
                <p className="text-2xl font-bold text-gray-900">
                  {forms.filter(app => app.status === 'completed').length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <FileText className="w-6 h-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">进行中</p>
                <p className="text-2xl font-bold text-gray-900">
                  {forms.filter(app => app.status === 'in_progress').length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-gray-100 rounded-lg">
                <FileText className="w-6 h-6 text-gray-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">草稿</p>
                <p className="text-2xl font-bold text-gray-900">
                  {forms.filter(app => app.status === 'draft').length}
                </p>
              </div>
            </div>
          </div>
        </div>

	        {/* 操作栏 */}
	        <div className="bg-white rounded-lg shadow mb-6">
	          <div className="px-6 py-4 border-b border-gray-200">
	            <div className="flex justify-between items-center">
	              <h2 className="text-lg font-medium text-gray-900">我的DS-160申请记录</h2>
	              <button
	                onClick={handleShowCreateModal}
	                className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
	              >
	                <Plus className="w-4 h-4" />
	                <span>创建DS-160申请</span>
	              </button>
	            </div>
	          </div>

          {/* 申请列表 */}
          <div className="p-6">
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-gray-600">加载中...</p>
              </div>
            ) : error ? (
              <div className="text-center py-8">
                <p className="text-red-600">{error}</p>
                <button
                  onClick={loadApplications}
                  className="mt-2 text-blue-600 hover:text-blue-700"
                >
                  重试
                </button>
              </div>
	            ) : forms.length === 0 ? (
	              <div className="text-center py-8">
	                <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
	                <h3 className="text-lg font-medium text-gray-900 mb-2">暂无DS-160申请记录</h3>
	                <p className="text-gray-600 mb-4">点击上方按钮创建您的第一个DS-160申请</p>
	                <button
	                  onClick={handleShowCreateModal}
	                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
	                >
	                  创建DS-160申请
	                </button>
	              </div>
	            ) : (
              <div className="space-y-4">
                {forms.map((form) => {
                  const statusStyles = getStatusStyles(form.status);
                  return (
                    <div
                      key={form._id}
                      className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-200"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className="text-lg font-medium text-gray-900">
                              {form.title}
                            </h3>
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusStyles.bg} ${statusStyles.text} ${statusStyles.border}`}>
                              {statusStyles.icon} {getStatusText(form.status)}
                            </span>
                          </div>

                          <div className="flex items-center space-x-6 text-sm text-gray-600">
                            <div className="flex items-center space-x-1">
                              <Calendar className="w-4 h-4" />
                              <span>创建时间: {formatDate(form.created_at)}</span>
                            </div>
                            <div className="flex items-center space-x-1">
                              <FileText className="w-4 h-4" />
                              <span>进度: {form?.completion_stats?.completion_rate || '0.0'}%</span>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleViewApplication(form._id)}
                            className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                            title="查看详情"
                          >
                            <Eye className="w-4 h-4" />
                          </button>

                          <button
                            onClick={() => handleEditApplication(form._id)}
                            className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors duration-200"
                            title="编辑申请"
                          >
                            <Edit className="w-4 h-4" />
                          </button>

                          {form.status === 'completed' && (
                            <button
                              onClick={() => handleDownloadConfirmation(form)}
                              className="p-2 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors duration-200"
                              title="下载确认页截图"
                            >
                              <Download className="w-4 h-4" />
                            </button>
                          )}

                          <button
                            onClick={() => setShowDeleteConfirm(form._id)}
                            className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                            title="删除申请"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 删除确认对话框 */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">确认删除</h3>
            <p className="text-gray-600 mb-6">
              您确定要删除这个申请吗？此操作无法撤销。
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg"
              >
                取消
              </button>
              <button
                onClick={() => handleDeleteApplication(showDeleteConfirm)}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg"
              >
                删除
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 修改密码模态框 */}
      {isChangePasswordOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full">
            <ChangePasswordForm
              onSuccess={() => {
                setIsChangePasswordOpen(false);
              }}
              onClose={() => {
                setIsChangePasswordOpen(false);
              }}
            />
          </div>
        </div>
      )}

      {/* 创建申请模态框 */}
      <CreateApplicationModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreateApplication={handleCreateApplication}
      />
    </div>
      <Footer variant="simple" showCopyright={false} />
    </>
  );
};

export default ApplicationManager;
